import { useState, useEffect } from 'react';

import { api } from '@/services';
import { useOAuth } from '@/hooks';
import { getErrorDetails } from '@/utils';
import { PLATFORM_CAPTIONS } from '@/constants';

const useAccountPlaylists = ({ source, target, onUnauthorized }) => {
  const [error, setError] = useState(null);
  const [playlists, setPlaylists] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    source: { [source]: sourceAuth },
  } = useOAuth();

  const {
    reset: sourceReset,
    accessToken: sourceToken,
    accountId: sourceAccountId,
  } = sourceAuth || {};

  const sourceAuthId = sourceToken || sourceAccountId;

  const resetState = () => {
    setError(null);
    setPlaylists(null);
    setIsLoading(false);
  };

  const resetAuth = () => {
    sourceReset && sourceReset();
  };

  useEffect(() => {
    resetState();
  }, [source, target, sourceAuthId]);

  const caption = PLATFORM_CAPTIONS[source];

  const getPlaylists = async () => {
    resetState();

    setIsLoading(true);

    try {
      setPlaylists(await api.getAccountPlaylists(source, sourceAuthId));
    } catch (error) {
      setPlaylists(null);

      const { is401, is403, is500, message } = getErrorDetails(error);

      if (is401) {
        onUnauthorized();
      } else if (is403) {
        resetAuth();
      } else if (is500) {
        setError(`Failed to get ${caption} playlists. Please try again.`);
      } else {
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const clearPlaylists = () => resetState();

  return { error, playlists, isLoading, getPlaylists, clearPlaylists };
};

export default useAccountPlaylists;
